// src/core/core.ts
// --- GSAP bootstrap (centralized) ---
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
// Expose for helpers/console that reference window.gsap
// (kept here so core guarantees availability before observers/renderers)
(window as any).gsap = gsap;
export { gsap, ScrollTrigger };
// ------------------------------------
// src/core.ts
import { parseMarkdown } from "core/parser";
import { renderBlogView } from "render/renderBlogView";
import { renderFirstSection } from "render/renderFirstSection";
import { getBaseUrl, initStoryCorePaths } from "utils/config";
import { STORYCORE_VERSION } from "core/version";

import { Z } from "utils/constants";
import { enableDebugLogging, log } from "../utils/debug";


let progressBar: HTMLDivElement | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

function createProgressBar() {
  progressBar = document.createElement("div");
  // Ensure correct stacking and non-interactive behavior
  progressBar.style.zIndex = String(Z.PROGRESS);
  progressBar.style.pointerEvents = "none";
  progressBar.setAttribute("aria-hidden", "true");
  progressBar.id = "storycore-progress-bar";
  document.body.appendChild(progressBar);
}

function updateProgressBar() {
  if (!progressBar) return;

  const wrapper = document.getElementById("scroll-wrapper");
  if (!wrapper) return;

  const scrollY = wrapper.scrollTop;
  const contentH = wrapper.scrollHeight;
  const viewportH = wrapper.clientHeight;
  const maxScroll = Math.max(0, contentH - viewportH);
  const progress = maxScroll > 0 ? scrollY / maxScroll : 0;

  const trackH = window.innerHeight;
  let handleH = trackH * (viewportH / Math.max(contentH, 1));
  const MIN_HANDLE = 32;
  const MAX_HANDLE = Math.max(32, Math.floor(trackH * 0.6));
  handleH = Math.max(MIN_HANDLE, Math.min(MAX_HANDLE, handleH));

  progressBar.style.height = `${Math.round(handleH)}px`;
  const travel = Math.max(0, trackH - handleH);
  const yPx = Math.max(0, Math.min(travel, Math.round(travel * progress)));
  progressBar.style.transform = `translateY(${yPx}px)`;

  if (progress <= 0) {
    progressBar.classList.remove("visible");
    if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
    return;
  }

  progressBar.classList.add("visible");
  if (hideTimeout) clearTimeout(hideTimeout);
  hideTimeout = setTimeout(() => {
    progressBar?.classList.remove("visible");
    hideTimeout = null;
  }, 500);
}

function attachScrollListeners() {
  const wrapper = document.getElementById("scroll-wrapper");
  if (!wrapper) return;

  wrapper.addEventListener("scroll", updateProgressBar, { passive: true });
  window.addEventListener("resize", updateProgressBar);
  window.addEventListener("orientationchange", updateProgressBar);
  updateProgressBar();
}

export function initStoryCore(
  baseUrl: string = getBaseUrl(),
  preParsedSections?: { attrs: Record<string, any>; content: string }[]
): void {
  initStoryCorePaths();

  const isReaderMode = window.location.search.includes("mode=read");
  const wrapper = document.getElementById("scroll-wrapper");
  const fileName = location.pathname.split("/").pop()?.replace(".html", ".md");
  const mode = new URLSearchParams(window.location.search).get("mode") || "immersive";

  log(`[CORE] Mode: ${mode}`);
  log(`[StoryCore] Booting version ${STORYCORE_VERSION}`);

  if (!wrapper || !fileName) {
    console.error("[CORE] Missing wrapper or filename");
    return;
  }

  if (!document.getElementById("storycore-progress-bar")) {
    createProgressBar();
    attachScrollListeners();
    updateProgressBar();
  }

  const render = async (sections: { attrs: any; content: string }[]): Promise<void> => {
    wrapper.innerHTML = "";
    (window as any).__storycore_sections = sections;

    // Core owns the first section element now
    const firstEl = document.createElement("section");
    firstEl.id = "section-0";
    firstEl.dataset.sectionIndex = "0";
    firstEl.style.minHeight = "100vh";
    firstEl.style.scrollSnapAlign = "start";
    firstEl.style.scrollSnapStop = "always";
    wrapper.appendChild(firstEl);

    if (mode === "read") {
      log("[CORE] Reader mode active — rendering blog view.");
      renderBlogView(sections);
      updateProgressBar();
      return;
    }

    if (mode === "storybook") {
      log("[CORE] Storybook mode scaffold — rendering not implemented yet.");
      wrapper.innerHTML = `<div class="text-white p-5">📘 Storybook mode is under construction.</div>`;
      updateProgressBar();
      return;
    }

    if (sections.length > 0) {
      log("[CORE] Using renderFirstSection for first section with attrs:", sections[0]?.attrs);
      await renderFirstSection(baseUrl, sections);
    } else {
      console.warn("[CORE] No sections to render");
    }
    updateProgressBar();
  };

  if (preParsedSections) {
    log("[CORE] Using pre-parsed sections:", preParsedSections[0]?.attrs);
    render(preParsedSections);
    return;
  }

  const contentPath = `${baseUrl}/content/${fileName}`;
  log(`[CORE] Fetching markdown from: ${contentPath}`);

  fetch(contentPath)
    .then(res => res.text())
    .then(async markdown => {
      const sections = parseMarkdown(markdown);
      if (!sections.length) {
        console.warn("[CORE] No sections parsed from markdown. Check markdown format.");
      }
      const hasAudio = sections[0]?.attrs?.audio;
      log("[CORE] Parsed sections:", sections);
      log("[CORE] First section attrs:", sections[0]?.attrs);

      if (!isReaderMode && !hasAudio) {
        log("[CORE] No audio found, but continuing immersive mode by default.");
      }

      await render(sections);
    })
    .catch(err => {
      wrapper.innerHTML = `<div class="text-red-500 p-5">Error loading story: ${err}</div>`;
      console.error("[CORE] Failed to load story:", err);
    });
}